import React from "react"
import { isAlphanumericKeyCode, Keys } from "@digits-shared/components/UI/Elements/Keys"
import useStateBoolean from "@digits-shared/hooks/useStateBoolean"

export function useTypeaheadState(
  containerRef: React.RefObject<HTMLElement | null>,
  disabled: boolean,
  onDelete?: () => void
) {
  const ref = React.useRef<HTMLInputElement | null>(null)
  const [defaultSearchTerm, setDefaultSearchTerm] = React.useState<string | undefined>(undefined)
  const {
    value: isTypeaheadShown,
    toggle: toggleShowTypeahead,
    setTrue: showTypeahead,
    setFalse: hide,
  } = useStateBoolean()

  const onClick = React.useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation()
      event.preventDefault()
      if (!disabled) {
        toggleShowTypeahead()
      }
    },
    [disabled, toggleShowTypeahead]
  )

  const hideTypeahead = React.useCallback(() => {
    if (containerRef.current && isTypeaheadShown) {
      // Allow user to continue tabbing through the page and retain focus on the input
      containerRef.current.focus()
    }
    hide()
  }, [containerRef, hide, isTypeaheadShown])

  const onKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      event.stopPropagation()
      const { code } = event
      if (!disabled) {
        switch (code) {
          case Keys.Enter:
          case Keys.ArrowDown: {
            toggleShowTypeahead()

            if (containerRef.current) {
              containerRef.current.focus()
            }
            break
          }

          case Keys.Delete: {
            hideTypeahead()
            onDelete?.()
            break
          }

          case Keys.Escape: {
            hideTypeahead()
            break
          }

          default: {
            if (isAlphanumericKeyCode(code) && !isTypeaheadShown) {
              toggleShowTypeahead()
              setDefaultSearchTerm(event.key)
            }
            break
          }
        }
      }
    },
    [disabled, toggleShowTypeahead, containerRef, isTypeaheadShown, onDelete, hideTypeahead]
  )

  React.useEffect(() => {
    if (!isTypeaheadShown) {
      setDefaultSearchTerm("")
    }
  }, [isTypeaheadShown, setDefaultSearchTerm])

  return React.useMemo(
    () => ({
      isTypeaheadShown,
      showTypeahead,
      hideTypeahead,
      onClick,
      onKeyDown,
      ref,
      defaultSearchTerm,
    }),
    [isTypeaheadShown, showTypeahead, hideTypeahead, onClick, onKeyDown, defaultSearchTerm]
  )
}
